import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hostMetadataQueryKey, type TableExplorerMetadataEditorProviders } from '../../../table-explorer/components/tableExplorerHostPorts';

export type HostCandidateColumn = Awaited<ReturnType<TableExplorerMetadataEditorProviders['listColumns']>>[number];

/**
 * The loose-FK target picker's inference read for a host with injected metadata providers (Azure Table): the top
 * candidate table's columns, under the host cache scope. A child the picker renders ONLY for such a host, so a SQL
 * picker registers no extra query observer (S4 review G, S1) — SQL infers through `useTableMetadata`, as at base.
 * Renders nothing; reports the columns (or `undefined` while none are known) to the picker.
 */
export function HostCandidateColumns({ metadataEditors, connectionId, database, schema, table, onColumns }: {
  metadataEditors: TableExplorerMetadataEditorProviders;
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  onColumns: (columns: HostCandidateColumn[] | undefined) => void;
}) {
  const { data } = useQuery({
    queryKey: hostMetadataQueryKey(metadataEditors, connectionId, 'candidate-columns', database, schema, table),
    queryFn: () => metadataEditors.listColumns({ connectionId, database, schema, table }),
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    onColumns(data);
    return () => onColumns(undefined);
  }, [data, onColumns]);
  return null;
}
